// What sits above a dashboard now that each one has its own address: its name, Month and
// Filters, "How to read this", and the way to the others. The title and the controls,
// and no sentence under the title: what the dashboard is for is one click away in "How
// to read this", and the question it answers is the title's hover text.
//
// It is drawn as a section front page's header is (pages/SectionFrontPage): the icon in a
// filled square, the title large beside it, the small print under the title, and the
// controls on the right as one set of white boxes.
//
// It replaces the strip of tabs. A person used to see every dashboard as a pill and had
// to guess which one they needed; here they see ONE dashboard and reach the rest grouped
// by subject under "Other dashboards".
//
// Names and words are not kept here: a built-in dashboard's name is dashLabel() of its
// DashTab, and its question, icon and help come from navigation/catalog.
import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, Info, LayoutDashboard } from 'lucide-react';
import { DASHBOARD_GROUPS, DASHBOARD_INFO } from '../../navigation/catalog';
import { DASHBOARDS_TAB_PATH, customDashboardPath, dashLabel, dashboardPath, type DashTab } from '../../navigation/dashboards';
import HowToReadPanel from './HowToReadPanel';

// One box for every control in the row: the same height, corner, border and shadow as
// the find box on a section front page.
const HEADER_BUTTON =
  'flex h-10 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
// What the page hands over for this row (its Month box and its Filters button) is drawn by
// the page, with its own border, white and shadow. Here it is given the row's height and
// corner, so the four read as one set. Only the shape: the Filters button's colour says
// whether a filter is on, and stays the page's.
const CONTROL_ROW =
  'relative flex flex-wrap items-center gap-2 [&>button]:h-10 [&>button]:rounded-xl [&>select]:h-10 [&>select]:rounded-xl';
const MENU_HEADING = 'px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400';
const MENU_LINK =
  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
const MENU_LINK_OPEN = 'bg-primary-50 font-medium text-primary-700';
const MENU_LINK_CLOSED = 'font-medium text-gray-700 hover:bg-gray-50 hover:text-primary-700';
/** Why an always-live dashboard has no Month and no Filters. On hover, not on the page. */
const LIVE_NOTE = 'Month and filters do not apply here: this list is always live.';
/**
 * The same, for an always-live dashboard the page still hands Filters to (Data Hygiene,
 * whose Role filter narrows its people): only the month does not apply there.
 */
const LIVE_NOTE_NO_MONTH = 'The month does not apply here: this list is always live.';

interface DashboardHeaderProps {
  /** The built-in dashboard that is open; null for one the team built. */
  tab: DashTab | null;
  /** A team-built dashboard's own title. Empty while the list of them is still loading. */
  customTitle?: string | null;
  /** Small print under the title: Deployment's "Last updated" and its status messages. */
  status?: ReactNode;
  /** Month and Filters, exactly as the page draws them. Left out where they do not apply. */
  controls?: ReactNode;
  /** Mark an always-live dashboard: two words under the title, and on hover why it has no Month (and, unless `controls` are given, no Filters). */
  liveNote?: boolean;
  /** The built-in dashboards this person holds, in dashboard order. */
  allowedTabs: readonly DashTab[];
  /** The team-built dashboards this person can see (their own drafts included). */
  customDashboards: ReadonlyArray<{ id: number; title: string; status: string }>;
  /** Which team-built dashboard is open, if one is. */
  activeCustomId?: number | null;
  /**
   * Leave out "How to read this" and "Other dashboards": a dashboard whose own buttons are
   * the whole row (Hiring, the product owner, 16 Oct 2026). Every other dashboard keeps both.
   */
  plain?: boolean;
}

export default function DashboardHeader({
  tab, customTitle, status, controls, liveNote = false, allowedTabs, customDashboards, activeCustomId = null, plain = false,
}: DashboardHeaderProps): ReactElement {
  const info = tab ? DASHBOARD_INFO[tab] : null;
  const Icon = info?.icon ?? LayoutDashboard;
  const name = tab ? dashLabel(tab) : customTitle;
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── How to read this ──
  // Open from the button, or because the address asks for it (?help=1: how page search
  // lands a person who looked up one of a dashboard's words on what it means).
  const [helpClicked, setHelpClicked] = useState(false);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const helpInAddress = searchParams.has('help');
  const helpOpen = helpClicked || searchParams.get('help') === '1';
  const closeHelp = useCallback(() => {
    setHelpClicked(false);
    // Taken out of the address too, or a refresh (or a copied link) would open it again.
    if (helpInAddress) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('help');
        return next;
      }, { replace: true });
    }
    helpButtonRef.current?.focus();
  }, [helpInAddress, setSearchParams]);

  // ── Other dashboards ──
  const [othersOpen, setOthersOpen] = useState(false);
  const othersRef = useRef<HTMLDivElement>(null);
  const othersButtonRef = useRef<HTMLButtonElement>(null);
  const othersMenuId = useId();

  useEffect(() => {
    if (!othersOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!othersRef.current?.contains(e.target as Node)) setOthersOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOthersOpen(false);
      othersButtonRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [othersOpen]);

  // Another dashboard has opened (a link here, or the browser's Back): nothing that was
  // open for the last one stays open over this one.
  useEffect(() => {
    setOthersOpen(false);
    setHelpClicked(false);
  }, [pathname]);

  // Tabbing on past the last link leaves the menu, so it closes behind the focus.
  const onOthersBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) setOthersOpen(false);
  };
  // Tab already walks the links; the arrow keys do too, as in any menu.
  const onOthersKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    if (!othersOpen) { setOthersOpen(true); return; }
    const links = Array.from(othersRef.current?.querySelectorAll<HTMLAnchorElement>('a[href]') ?? []);
    if (links.length === 0) return;
    const at = links.indexOf(document.activeElement as HTMLAnchorElement);
    const next = e.key === 'ArrowDown' ? (at + 1) % links.length : (at <= 0 ? links.length - 1 : at - 1);
    links[next].focus();
  };

  const groups = DASHBOARD_GROUPS
    .map((group) => ({ ...group, tabs: allowedTabs.filter((t) => DASHBOARD_INFO[t].group === group.id) }))
    .filter((group) => group.tabs.length > 0);

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
      {/* basis + grow: on a narrower window a long title wraps beside the buttons before
          the buttons are pushed onto a line of their own. On a phone the title's 18rem
          leaves the buttons no room beside it, so they always drop under it. */}
      <div className="flex min-w-0 flex-1 basis-72 items-center gap-3">
        {/* The dashboard's icon in its filled square, as on a section front page. */}
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          {/* The question the dashboard answers is no longer a sentence under the title: it
              is the title's hover text. */}
          <h1 className="break-words text-3xl font-bold tracking-tight text-gray-900" title={info?.question}>
            {/* A team-built dashboard's name arrives with the list of them: its place is held
                meanwhile, as a span because a heading may not hold a block. */}
            {name || <span aria-hidden className="cp-shimmer inline-block h-8 w-56 max-w-full rounded-md align-middle" />}
          </h1>
          {status && <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3">{status}</div>}
          {liveNote && (
            <p className="mt-0.5 text-xs text-gray-400" title={controls ? LIVE_NOTE_NO_MONTH : LIVE_NOTE}>Always live</p>
          )}
        </div>
      </div>

      {/* `relative` is here, not on the menu's own button: the menu lines up with the right
          edge of this whole row, so on a phone, where the buttons wrap, it cannot hang off
          the side of the screen. */}
      <div className={CONTROL_ROW}>
        {controls}
        {tab && !plain && (
          <button
            ref={helpButtonRef}
            type="button"
            onClick={() => { setOthersOpen(false); setHelpClicked(true); }}
            className={HEADER_BUTTON}
          >
            <Info className="h-4 w-4" aria-hidden />
            How to read this
          </button>
        )}
        {!plain && (
        <div ref={othersRef} onBlur={onOthersBlur} onKeyDown={onOthersKeyDown}>
          <button
            ref={othersButtonRef}
            type="button"
            onClick={() => setOthersOpen((open) => !open)}
            aria-expanded={othersOpen}
            aria-controls={othersOpen ? othersMenuId : undefined}
            className={HEADER_BUTTON}
          >
            Other dashboards
            <ChevronDown className={`h-4 w-4 transition-transform ${othersOpen ? 'rotate-180' : ''}`} aria-hidden />
          </button>
          {othersOpen && (
            <nav
              id={othersMenuId}
              aria-label="Other dashboards"
              // z: over the tables' sticky Actions cells (z-20), under the top bar (z-30).
              className="absolute right-0 top-full z-[25] mt-1.5 flex max-h-[70vh] w-72 max-w-full flex-col overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg"
            >
              <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                {groups.map((group) => (
                  <div key={group.id}>
                    <p className={MENU_HEADING}>{group.name}</p>
                    {group.tabs.map((t) => {
                      const TabIcon = DASHBOARD_INFO[t].icon;
                      return (
                        <Link
                          key={t}
                          // Its BARE address, on purpose. ?list and ?help belong to the
                          // dashboard that is open: carried along, the next one would be
                          // handed a list that is not its own, and the help of the last.
                          to={dashboardPath(t)}
                          onClick={() => setOthersOpen(false)}
                          aria-current={t === tab ? 'page' : undefined}
                          className={`${MENU_LINK} ${t === tab ? MENU_LINK_OPEN : MENU_LINK_CLOSED}`}
                        >
                          <TabIcon className="h-4 w-4 flex-shrink-0" aria-hidden />
                          {dashLabel(t)}
                        </Link>
                      );
                    })}
                  </div>
                ))}
                {customDashboards.length > 0 && (
                  <div>
                    <p className={MENU_HEADING}>Built by your team</p>
                    {customDashboards.map((d) => (
                      <Link
                        key={d.id}
                        to={customDashboardPath(d.id)}
                        onClick={() => setOthersOpen(false)}
                        aria-current={d.id === activeCustomId ? 'page' : undefined}
                        className={`${MENU_LINK} ${d.id === activeCustomId ? MENU_LINK_OPEN : MENU_LINK_CLOSED}`}
                      >
                        <LayoutDashboard className="h-4 w-4 flex-shrink-0" aria-hidden />
                        <span className="min-w-0 truncate">{d.title}</span>
                        {/* Drafts reach only their author; the pill says why nobody else has it. */}
                        {d.status !== 'published' && (
                          <span className="ml-auto flex-shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">
                            Draft
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* Outside the part that scrolls: the way back to all of them should not be
                  the one link a long list hides. */}
              <div className="border-t border-gray-100 p-1.5">
                <Link
                  // The home screen's Dashboards tab, where every one of them is: not its default "Needs attention".
                  to={DASHBOARDS_TAB_PATH}
                  onClick={() => setOthersOpen(false)}
                  className={`${MENU_LINK} font-semibold text-gray-700 hover:bg-gray-50 hover:text-primary-700`}
                >
                  All dashboards
                </Link>
              </div>
            </nav>
          )}
        </div>
        )}
      </div>

      {tab && helpOpen && <HowToReadPanel tab={tab} onClose={closeHelp} />}
    </div>
  );
}
